import { useState, useRef } from "react";
import {
  Upload,
  ChevronRight,
  ChevronDown,
  Download,
  CheckCircle2,
  AlertTriangle,
  X,
  Ban,
} from "lucide-react";
import { PostLoadCard } from "@/components/post-load/PostLoadCard";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useVerificationWarning } from "@/hooks/useVerificationWarning";
import { VerificationWarningModal } from "@/components/verification/VerificationWarningModal";
import {
  downloadTruckTemplateCSV,
  parseAndValidateTruckCSV,
  geocodeCity,
  buildTruckPayload,
  type BulkTruckParseResult,
  isAnywhereDestination,
} from "@/lib/bulkTruckUploadSchema";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SOFT_WARN_ROWS = 50;
const MAX_BULK_ROWS = 100;
const ROW_TIMEOUT_MS = 15_000;

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PostResult {
  posted: number;
  skipped: number;
  failed: { rowNumber: number; error: string }[];
  cancelled: number;
  total: number;
}

interface PostingProgress {
  current: number;
  total: number;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Row timed out after ${ms / 1000} seconds`)), ms),
    ),
  ]);
}

function geoKey(city: string, state: string): string {
  return `${city.trim().toLowerCase()}|${state.trim().toUpperCase()}`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function BulkUploadTrucksCard() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<BulkTruckParseResult | null>(null);
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState<PostResult | null>(null);
  const [postingProgress, setPostingProgress] = useState<PostingProgress | null>(null);
  const [rowLimitWarning, setRowLimitWarning] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { showWarningOrRun, modalProps } = useVerificationWarning();

  /* ---------------- Row limit ---------------- */

  const checkRowLimit = (totalRows: number): boolean => {
    if (totalRows > MAX_BULK_ROWS) {
      toast({
        title: `Maximum ${MAX_BULK_ROWS} rows per upload`,
        description: "Please split your file into smaller batches.",
        variant: "destructive",
      });
      setRowLimitWarning(null);
      return false;
    }
    if (totalRows > SOFT_WARN_ROWS) {
      setRowLimitWarning(`Large upload (${totalRows} rows) — processing may take 30+ seconds`);
    } else {
      setRowLimitWarning(null);
    }
    return true;
  };

  /* ---------------- CSV handlers ---------------- */

  const handleDownload = () => {
    downloadTruckTemplateCSV();
    toast({
      title: "Truck template downloaded",
      description: "Fill in your truck postings and upload the CSV.",
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const parsed = parseAndValidateTruckCSV(text);

      if (!checkRowLimit(parsed.totalRows)) {
        e.target.value = "";
        return;
      }

      setResult(parsed);
      setPostResult(null);
      toast({
        title: `${parsed.totalRows} row${parsed.totalRows !== 1 ? "s" : ""} parsed`,
        description: `${parsed.validRows.length} valid · ${parsed.invalidRows.length} invalid`,
      });
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const clearAll = () => {
    setResult(null);
    setPostResult(null);
    setPostingProgress(null);
    setRowLimitWarning(null);
    cancelRef.current = false;
  };

  /* ---------------- Posting ---------------- */

  const handlePost = async () => {
    if (!result || result.validRows.length === 0 || !user) return;
    setPosting(true);
    setPostingProgress({ current: 0, total: result.validRows.length });
    cancelRef.current = false;

    let companyId: string | null = null;
    try {
      const { data } = await supabase.rpc("get_user_companies", { user_uuid: user.id });
      if (data && data.length > 0) companyId = data[0];
    } catch {
      /* optional */
    }

    if (!companyId) {
      toast({
        title: "No company found",
        description: "You need to be part of a verified company to post trucks.",
        variant: "destructive",
      });
      setPosting(false);
      setPostingProgress(null);
      return;
    }

    /* --- Geocode dedup --- */
    const uniqueKeys = new Set<string>();
    for (const row of result.validRows) {
      uniqueKeys.add(geoKey(row.data.origin_city, row.data.origin_state));
      if (!row.normalised?.anywhere) {
        uniqueKeys.add(geoKey(row.data.destination_city, row.data.destination_state));
      }
    }

    const geoCache = new Map<string, Awaited<ReturnType<typeof geocodeCity>>>();
    for (const key of uniqueKeys) {
      if (cancelRef.current) break;
      const [city, state] = key.split("|");
      try {
        const geo = await geocodeCity(city, state);
        geoCache.set(key, geo);
      } catch {
        geoCache.set(key, null);
      }
    }

    /* --- Row-by-row insert --- */
    const failed: { rowNumber: number; error: string }[] = [];
    let posted = 0;
    let cancelled = 0;

    for (let i = 0; i < result.validRows.length; i++) {
      if (cancelRef.current) {
        cancelled = result.validRows.length - i;
        break;
      }

      const row = result.validRows[i];
      setPostingProgress({ current: i + 1, total: result.validRows.length });

      try {
        await withTimeout(
          (async () => {
            const originKey = geoKey(row.data.origin_city, row.data.origin_state);
            const origin = geoCache.get(originKey) ?? null;
            if (!origin) {
              failed.push({
                rowNumber: row.rowNumber,
                error: `Could not geocode origin "${row.data.origin_city}, ${row.data.origin_state}"`,
              });
              return;
            }

            let destination: Awaited<ReturnType<typeof geocodeCity>> = null;
            if (!row.normalised?.anywhere) {
              const destKey = geoKey(row.data.destination_city, row.data.destination_state);
              destination = geoCache.get(destKey) ?? null;
              if (!destination) {
                failed.push({
                  rowNumber: row.rowNumber,
                  error: `Could not geocode destination "${row.data.destination_city}, ${row.data.destination_state}"`,
                });
                return;
              }
            }

            const payload = buildTruckPayload(row, origin, destination, companyId!, user.id);
            const { error } = await supabase
              .from("truck_postings")
              .insert(payload as any);
            if (error) {
              failed.push({ rowNumber: row.rowNumber, error: error.message });
              return;
            }
            posted++;
          })(),
          ROW_TIMEOUT_MS,
        );
      } catch (err: any) {
        failed.push({ rowNumber: row.rowNumber, error: err?.message || "Unknown error" });
      }
    }

    const summary: PostResult = {
      posted,
      skipped: result.invalidRows.length,
      failed,
      cancelled,
      total: result.totalRows,
    };
    setPostResult(summary);
    setPosting(false);
    setPostingProgress(null);

    const cancelledNote = cancelled > 0 ? ` · ${cancelled} cancelled` : "";
    toast({
      title: `${posted} truck${posted !== 1 ? "s" : ""} posted`,
      description:
        failed.length > 0
          ? `${failed.length} failed${cancelledNote} — see details below`
          : `All valid rows posted successfully.${cancelledNote}`,
      variant: failed.length > 0 ? "destructive" : "default",
    });
  };

  const handleCancel = () => {
    cancelRef.current = true;
  };

  /* ---------------- Render ---------------- */

  const hasResult = result !== null;
  const hasPostResult = postResult !== null;
  const progressPct = postingProgress
    ? Math.round((postingProgress.current / postingProgress.total) * 100)
    : 0;

  // suppress unused-import lint for helper kept for parity
  void isAnywhereDestination;

  return (
    <>
    <PostLoadCard>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <h4 className="text-sm font-semibold text-foreground">Bulk Upload Trucks</h4>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      <div
        className="overflow-hidden transition-all duration-200"
        style={{
          maxHeight: open ? (hasResult || hasPostResult ? 800 : 500) : 0,
          opacity: open ? 1 : 0,
        }}
      >
        <div className="px-4 pb-3 space-y-3">
          {/* ---- Initial state ---- */}
          {!hasResult && !hasPostResult && (
            <>
              <p className="text-xs text-muted-foreground">
                Download the LaneRunner truck CSV template, fill in your trucks, and upload to post in bulk.
                <span className="block mt-0.5 text-[10px]">
                  Maximum {MAX_BULK_ROWS} rows per upload. Use "Anywhere" in destination_city for open lanes.
                </span>
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownload}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
                >
                  <Download className="h-3 w-3" />
                  Download Template
                </button>
                <button
                  onClick={() => showWarningOrRun(() => fileRef.current?.click())}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
                >
                  <Upload className="h-3 w-3" />
                  Upload CSV
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
              <p className="text-[10px] text-muted-foreground">CSV only · one row = one truck</p>
            </>
          )}

          {/* ---- Preview state ---- */}
          {hasResult && !hasPostResult && result && (
            <div className="space-y-2">
              {rowLimitWarning && (
                <div className="flex items-center gap-2 text-[11px] font-medium px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                  {rowLimitWarning}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-semibold text-foreground">
                    {result.totalRows} row{result.totalRows !== 1 ? "s" : ""}
                  </span>
                  <span
                    className="inline-flex items-center gap-1"
                    style={{ color: "hsl(var(--brand-teal-deep))" }}
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    {result.validRows.length} valid
                  </span>
                  {result.invalidRows.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-destructive">
                      <AlertTriangle className="h-3 w-3" />
                      {result.invalidRows.length} invalid
                    </span>
                  )}
                </div>
                <button
                  onClick={clearAll}
                  disabled={posting}
                  className="text-[10px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <X className="h-3 w-3" /> Clear
                </button>
              </div>

              {result.invalidRows.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1.5 rounded-md border border-border bg-muted/40 p-2">
                  {result.invalidRows.map((ir) => (
                    <div key={ir.row.rowNumber} className="text-[10px]">
                      <span className="font-semibold text-destructive">Row {ir.row.rowNumber}:</span>
                      {ir.errors.map((err, idx) => (
                        <span key={idx} className="ml-1.5 text-muted-foreground">
                          {err}
                          {idx < ir.errors.length - 1 ? ";" : ""}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {result.validRows.length > 0 && !posting && (
                <div className="rounded-md border border-border bg-muted/40 p-2">
                  <p className="text-[10px] text-muted-foreground">
                    {result.validRows.length} truck{result.validRows.length !== 1 ? "s" : ""} ready to post.
                    {result.invalidRows.length > 0 ? " Invalid rows will be skipped." : ""}
                  </p>
                </div>
              )}

              {posting && postingProgress && (
                <div className="space-y-1.5">
                  <Progress value={progressPct} className="h-2" />
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-muted-foreground">
                      Posting row {postingProgress.current} of {postingProgress.total}…
                    </p>
                    <button
                      onClick={handleCancel}
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors inline-flex items-center gap-1"
                    >
                      <Ban className="h-3 w-3" />
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {!posting && (
                <div className="flex items-center gap-2 flex-wrap">
                  {result.validRows.length > 0 && (
                    <button
                      onClick={() => showWarningOrRun(() => handlePost())}
                      className="text-[11px] font-semibold px-3 py-1.5 rounded-md transition-colors text-primary-foreground inline-flex items-center gap-1.5"
                      style={{ backgroundColor: "hsl(var(--brand-teal-deep))" }}
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      Post Valid Trucks
                    </button>
                  )}
                  <button
                    onClick={() => showWarningOrRun(() => fileRef.current?.click())}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
                  >
                    <Upload className="h-3 w-3" /> Upload New CSV
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              )}
            </div>
          )}

          {/* ---- Result summary ---- */}
          {hasPostResult && postResult && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-foreground">Bulk Post Complete</p>

              <div className="flex items-center gap-3 text-xs flex-wrap">
                <span className="font-semibold text-foreground">{postResult.total} total</span>
                <span
                  className="inline-flex items-center gap-1"
                  style={{ color: "hsl(var(--brand-teal-deep))" }}
                >
                  <CheckCircle2 className="h-3 w-3" /> {postResult.posted} posted
                </span>
                {postResult.skipped > 0 && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <AlertTriangle className="h-3 w-3" /> {postResult.skipped} skipped
                  </span>
                )}
                {postResult.failed.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-destructive">
                    <X className="h-3 w-3" /> {postResult.failed.length} failed
                  </span>
                )}
                {postResult.cancelled > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <Ban className="h-3 w-3" /> {postResult.cancelled} cancelled
                  </span>
                )}
              </div>

              {postResult.failed.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1.5 rounded-md border border-border bg-muted/40 p-2">
                  {postResult.failed.map((f) => (
                    <div key={f.rowNumber} className="text-[10px]">
                      <span className="font-semibold text-destructive">Row {f.rowNumber}:</span>
                      <span className="ml-1.5 text-muted-foreground">{f.error}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={clearAll}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PostLoadCard>
    <VerificationWarningModal {...modalProps} />
    </>
  );
}
